<!--

 -->

<template>
  <div>
    <div class="gsl-hrm-background-container">
      <div class="gsl-hrm-paper-container">
        <div class="gsl-hrm-header-container">
          <oxd-text tag="h6" class="gsl-hrm-main-title">
            {{
              myLeaveRequest
                ? $t('leave.my_leave_request_details')
                : $t('leave.leave_request_details')
            }}
          </oxd-text>
        </div>
        <oxd-divider class="gsl-hrm-horizontal-margin gsl-hrm-clear-margins" />
        <br />
        <div class="gsl-hrm-horizontal-padding">
          <oxd-grid :cols="3">
            <oxd-grid-item>
              <oxd-input-group :label="$t('general.employee_name')">
                <oxd-text class="gsl-hrm-request-details-text" tag="p">
                  {{ employeeName }}
                </oxd-text>
              </oxd-input-group>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-group :label="$t('leave.requested_for')">
                <oxd-text class="gsl-hrm-request-details-text" tag="p">
                  {{ leavePeriod }}
                </oxd-text>
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </div>
        <br />
        <table-header
          :selected="0"
          :total="total"
          :loading="isLoading"
        ></table-header>
        <div class="gsl-hrm-container">
          <oxd-card-table
            :headers="headers"
            :items="response && response.data"
            :selectable="false"
            :clickable="false"
            :loading="isLoading"
            class="gsl-hrm-leave-list"
            row-decorator="oxd-table-decorator-card"
          />
        </div>
        <div class="gsl-hrm-bottom-container">
          <span>
            <oxd-button
              display-type="ghost"
              :label="$t('general.back')"
              @click="onClickBack"
            />
            <oxd-button
              class="gsl-hrm-left-space"
              display-type="secondary"
              icon-name="chat-right-text-fill"
              :label="$t('general.comments')"
              @click="onClickComments"
            />
          </span>
          <oxd-pagination
            v-if="showPaginator"
            v-model:current="currentPage"
            :length="pages"
          />
        </div>
      </div>
    </div>
    <leave-comment-modal
      v-if="showCommentModal"
      :id="commentModalState"
      :leave-request="isLeaveRequest"
      @close="onCommentModalClose"
    >
    </leave-comment-modal>

    <leave-action-log-modal
      v-if="showLeaveActionLogModal"
      :data="leaveActionLog"
      @close="onLeaveActionLogModalClose"
    ></leave-action-log-modal>

    <approval-confirmation
      ref="approvalDialog"
      :title="$t('leave.leave_approval_confirmation')"
      :subtitle="$t('leave.leave_approval_confirmation_subtitle')"
      :content="listOfSubordinatesState"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('leave.yes_approve')"
    ></approval-confirmation>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {truncate} from '@ohrm/core/util/helper/truncate';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useLeaveActions from '@/orangehrmLeavePlugin/util/composable/useLeaveActions';
import LeaveCommentsModal from '@/orangehrmLeavePlugin/components/LeaveCommentsModal';
import LeaveActionLogModal from '@/orangehrmLeavePlugin/components/LeaveActionLogModal';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import ConfirmationDialog from '@/orangehrmLeavePlugin/components/LeaveApprovalConfirmationModel';

export default {
  name: 'LeaveViewRequest',

  components: {
    'leave-comment-modal': LeaveCommentsModal,
    'leave-action-log-modal': LeaveActionLogModal,
    'approval-confirmation': ConfirmationDialog,
  },

  props: {
    leaveRequestId: {
      type: [String, Number],
      required: true,
    },
    myLeaveRequest: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/leave-requests/${props.leaveRequestId}/leaves`,
    );

    const {leaveActions, processLeaveAction} = useLeaveActions(http);
    const {$t} = usei18n();
    const {jsDateFormat} = useDateFormat();
    const {locale} = useLocale();
    const {$tEmpName} = useEmployeeNameTranslate();

    const leaveRequestNormalizer = (data) => {
      return data.map((item) => {
        let leaveDatePeriod = '';
        const duration = item.dates.durationType?.type;

        if (item.dates.fromDate) {
          leaveDatePeriod = formatDate(
            parseDate(item.dates.fromDate),
            jsDateFormat,
            {locale},
          );
        }
        if (item.dates.startTime && item.dates.endTime) {
          leaveDatePeriod += ` (${item.dates.startTime} - ${item.dates.endTime})`;
        }
        if (
          duration === 'half_day_morning' ||
          duration === 'half_day_afternoon'
        ) {
          leaveDatePeriod += ` ${$t('leave.half_day')}`;
        }

        const leaveTypeName = item.leaveType?.name;
        if (item.leaveType?.deleted) {
          leaveTypeName + $t('general.deleted');
        }

        let actionByCombined = [];

        if (Array.isArray(item.actionBy)) {
          actionByCombined = item.actionBy.map((action) => ({
            name: action.employee,
            status: action.status,
            createdAt: action.createdAt,
          }));
        }

        return {
          id: item.id,
          date: leaveDatePeriod,
          leaveType: leaveTypeName,
          leaveBalance: item.leaveBalance?.balance.balance
            ? parseFloat(item.leaveBalance.balance.balance).toFixed(2)
            : undefined,
          duration: parseFloat(item.lengthHours).toFixed(2),
          status: item.leaveStatus?.name,
          comment: truncate(item.lastComment?.comment),
          actionByCombined,
          actions: item.allowedActions,
          canComment: !(
            item.leaveStatus?.id === 5 || item.leaveStatus?.id === 4
          ),
        };
      });
    };

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {normalizer: leaveRequestNormalizer});

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      response,
      leaveActions,
      processLeaveAction,
      jsDateFormat,
      locale,
      translateEmpName: $tEmpName,
    };
  },

  data() {
    return {
      headers: [
        {name: 'date', title: this.$t('general.date'), style: {flex: 1}},
        {
          name: 'leaveType',
          title: this.$t('leave.leave_type'),
          style: {flex: 1},
        },
        {
          name: 'leaveBalance',
          title: this.$t('leave.leave_balance_days'),
          style: {flex: 1},
        },
        {
          name: 'duration',
          title: this.$t('attendance.duration_hours'),
          style: {flex: 1},
        },
        {name: 'status', title: this.$t('general.status'), style: {flex: 1}},
        {
          name: 'comment',
          title: this.$t('general.comments'),
          style: {flex: '10%'},
        },
        {
          name: 'actionByCombined',
          title: this.$t('general.action_by'),
          style: {flex: '5%'},
          cellRenderer: this.getActionBy,
        },
        {
          name: 'action',
          slot: 'footer',
          title: this.$t('general.actions'),
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
          style: {flex: '20%'},
        },
      ],
      showCommentModal: false,
      commentModalState: null,
      isLeaveRequest: false,
      showLeaveActionLogModal: false,
      leaveActionLog: null,
      listOfSubordinatesState: {},
    };
  },

  computed: {
    employeeName() {
      const employee = this.response?.meta?.employee;
      if (employee) {
        return this.translateEmpName(employee, {
          includeMiddle: true,
          excludePastEmpTag: false,
        });
      }
      return '';
    },
    leavePeriod() {
      const startDate = formatDate(
        parseDate(this.response?.meta?.startDate),
        this.jsDateFormat,
        {locale: this.locale},
      );
      const endDate = formatDate(
        parseDate(this.response?.meta?.endDate),
        this.jsDateFormat,
        {locale: this.locale},
      );
      return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
    },
  },

  methods: {
    getActionBy(index, item, header, row) {
      const names = row.actionByCombined
        .map((entry) => {
          const color =
            entry.status === 2 || entry.status === -2
              ? 'green'
              : entry.status === 0
              ? '#ff7f08'
              : entry.status === 4
              ? '#146da0'
              : entry.status === 1
              ? '#f7ac23'
              : 'red';
          return `<span style="color: ${color}; font-weight: bold; margin-right: 4px; cursor: pointer" title="${entry.name}">${entry.name}</span>`;
        })
        .join(', ');

      return {
        component: 'span',
        props: {
          innerHTML: names,
          onClick: () => this.onClickActionBy(row),
        },
      };
    },
    onClickActionBy(row) {
      this.leaveActionLog = row?.actionByCombined;
      this.showLeaveActionLogModal = true;
    },
    cellRenderer(...[, , , row]) {
      const cellConfig = {};
      const dropdownActions = [];
      const {approve, reject, cancel, more} = this.leaveActions;

      if (row.canComment) {
        dropdownActions.push({
          label: 'Add Comment',
          context: 'add_comment',
        });
      }

      row.actions.map((item) => {
        if (item.action === 'APPROVE') {
          approve.props.label = this.$t('general.approve');
          approve.props.onClick = () => this.onLeaveAction(row.id, 'APPROVE');
          cellConfig.approve = approve;
        }
        if (item.action === 'REJECT') {
          reject.props.label = this.$t('general.reject');
          reject.props.onClick = () => this.onLeaveAction(row.id, 'REJECT');
          cellConfig.reject = reject;
        }
        if (item.action === 'CANCEL') {
          if (this.myLeaveRequest) {
            cancel.props.label = this.$t('general.cancel');
            cancel.props.onClick = () => this.onLeaveAction(row.id, 'CANCEL');
            cellConfig.cancel = cancel;
          } else {
            dropdownActions.push({
              label: 'Cancel Leave',
              context: 'cancel_leave',
            });
          }
        }
      });

      if (dropdownActions.length > 0) {
        more.props.options = dropdownActions;
        more.props.onClick = ($event) =>
          this.onLeaveDropdownAction($event, row);
        cellConfig.more = more;
      }

      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onClickComments() {
      this.commentModalState = this.leaveRequestId;
      this.isLeaveRequest = true;
      this.showCommentModal = true;
    },
    onCommentModalClose() {
      this.commentModalState = null;
      this.showCommentModal = false;
      this.resetDataTable();
    },
    onLeaveActionLogModalClose() {
      this.showLeaveActionLogModal = false;
    },
    onLeaveDropdownAction(event, item) {
      if (event.context === 'cancel_leave') {
        this.onLeaveAction(item.id, 'CANCEL');
      } else {
        this.commentModalState = item.id;
        this.isLeaveRequest = false;
        this.showCommentModal = true;
      }
    },
    async onLeaveAction(id, actionType) {
      this.isLoading = true;

      if (actionType === 'APPROVE') {
        const response = await this.http.request({
          method: 'GET',
          url: `/api/v2/leave/leave-employee/${this.leaveRequestId}/validation/subordinate`,
        });

        const {data} = response.data;
        if (data.valid) {
          this.listOfSubordinatesState = data?.list || {};
          const confirmation = await this.$refs.approvalDialog.showDialog();

          if (confirmation !== 'ok') {
            this.isLoading = false;
            return;
          }
        }
      }
      this.processLeaveAction(id, actionType)
        .then(() => {
          this.$toast.updateSuccess();
        })
        .finally(this.resetDataTable);
    },
    onClickBack() {
      this.myLeaveRequest
        ? navigate('/leave/viewMyLeaveList')
        : navigate('/leave/viewLeaveList');
    },
    async resetDataTable() {
      await this.execQuery();
    },
  },
};
</script>

<style lang="scss" scoped>
.gsl-hrm-bottom-container {
  align-items: center;
  justify-content: space-between;
}
.gsl-hrm-request-details-text {
  font-size: $oxd-input-control-font-size;
}
::v-deep(.card-footer-slot) {
  .oxd-table-cell-actions {
    justify-content: flex-end;
  }
  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}

.gsl-hrm-leave-list {
  ::v-deep(.card-header-slot) {
    border-bottom: $oxd-input-control-border--active;
    margin-bottom: 5px;
  }

  ::v-deep(.card-body-slot) {
    display: unset;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 0.2rem;
  }
}
</style>
